/**
 * CopilotPanel — der frei schwebende KI-Assistent (Redesign 28.08.2026, Antoine: "schwebendes
 * Fenster, das ich überall hinziehen kann und in der Größe/Form jederzeit verändern kann", nach
 * einem freigegebenen Apple-Glas-Prototyp). Nutzt die geteilte Session aus dem CopilotProvider,
 * rendert Verlauf (messages) + Agent-Kacheln (ui/agent-cards) und ein Eingabefeld mit Datei-Anhang
 * (wie pages/assistent.tsx). Ab lg ein frei ziehbares/skalierbares Glas-Fenster mit Rand-Andocken
 * (Geometrie kommt aus CopilotProvider.floatWin — siehe dortigen Kommentar, warum der Hook seit der
 * App-weiten Erweiterung 28.08.2026 dort statt lokal hier instanziiert wird), darunter unverändert
 * die bestehende Vollbild-Schublade von rechts (kein frei schwebendes Fenster auf Mobil — dafür ist
 * dort kein Platz und keine Maus zum Ziehen).
 * Animationen respektieren prefers-reduced-motion (motion-safe-Präfix).
 *
 * Leerzustand (Redesign 2026-07): namentliche Begrüßung (tageszeitabhängig, wie claude.ai) +
 * Vorschlags-Karten "Womit soll ich anfangen?" — der Assistent bedient die ganze Software.
 */
import { ChatText } from "./ChatText";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useUser } from "@stackframe/react";
import { Button, Icon, ErrorNote, Logo, useBestaetigung } from "./components";
import type { ResizeCorner } from "../hooks/useFloatingWindow";
import { useOverlayOpenSignal } from "../hooks/useOverlayOpen";
import { CardView } from "./agent-cards";
import { KompositionsFortschritt, useLaufendeKomposition } from "./komposition-fortschritt";
import { useCopilot, type AgentConversationSummary } from "./CopilotProvider";
import { textOf, stripMarkdown, internalContextLabel } from "../hooks/useAgentSession";
import { apiFetch } from "../lib/api";

/** Ein Zustandsbefund aus `GET /api/agent/befunde` (E4.7). Handlung ist Pflicht, nicht optional. */
interface Befund {
  kennung: string;
  schwere: "hoch" | "mittel" | "niedrig";
  klartext: string;
  zahl: number;
  handlung: { text: string; ziel: string };
}
import { useApi, apiMutate } from "../hooks/useApi";
import type { AutoTask, AutoTaskKind, TasksBoard } from "./types";
import { angebotsVorschlag } from "../lib/aufgaben-liste";
import { amThemaDran, zeigeVorschlaege, vorschlagsSignatur } from "./vorschlags-anzeige";

/**
 * Die vier Skalier-Griffe (Prototyp Z.493-504) — Position exakt in der jeweiligen Rundungsecke,
 * Cursor je nach Diagonale (nw/se = nwse, ne/sw = nesw). Tailwind-Klassen hier bewusst als
 * STATISCHE Literale (nicht aus `corner` zusammengesetzt) — Tailwinds Build-Scan braucht den
 * vollständigen Klassennamen wörtlich im Quelltext.
 */
const RESIZE_CORNERS: { corner: ResizeCorner; label: string; pos: string; cursor: string; rotate: string }[] = [
  { corner: "nw", label: "oben links", pos: "top-0 left-0", cursor: "cursor-nwse-resize", rotate: "rotate-[270deg]" },
  { corner: "ne", label: "oben rechts", pos: "top-0 right-0", cursor: "cursor-nesw-resize", rotate: "rotate-0" },
  { corner: "sw", label: "unten links", pos: "bottom-0 left-0", cursor: "cursor-nesw-resize", rotate: "rotate-180" },
  { corner: "se", label: "unten rechts", pos: "bottom-0 right-0", cursor: "cursor-nwse-resize", rotate: "rotate-90" },
];

const MAX_FILE_BYTES = 8 * 1024 * 1024;
// Datei-Anhang: Bilder/PDF (Speisekarten-Import) + Tabellen (Equipment/Personal-Import, Fix #3).
const FILE_ACCEPT =
  "application/pdf,image/*,.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv";

/**
 * Leerzustand-Vorschläge (Redesign): je Karte ein Icon, ein Titel und ein Beispielsatz. Ein Klick
 * füllt das Eingabefeld mit dem Beispiel (der Nutzer passt Namen/Zahlen an, bevor er sendet) — außer
 * "Speisekarte einlesen", das direkt den Datei-Dialog öffnet (braucht eine Datei).
 */
const EMPTY_SUGGESTIONS: { icon: string; label: string; example: string; action: "prompt" | "upload" }[] = [
  { icon: "description", label: "Speisekarte einlesen", example: "Lies diese Karte ein und prüf die Allergene", action: "upload" },
  { icon: "request_quote", label: "Angebot schreiben", example: "Angebot für Brandt: 80 Gäste, Fingerfood", action: "prompt" },
  { icon: "forum", label: "Nachfassen & antworten", example: "Erinnere Kessler freundlich an das Angebot", action: "prompt" },
  { icon: "insights", label: "Nachschlagen & rechnen", example: "Wie lief der Juni gegenüber Mai?", action: "prompt" },
];

/**
 * Fähigkeiten-Browser (23c): durchsuchbarer Katalog aller Chat-Fähigkeiten, gruppiert nach
 * Bereich. Ein Klick auf einen Beispielsatz übernimmt ihn ins Eingabefeld (wie EMPTY_SUGGESTIONS)
 * und wechselt zurück in die normale Chat-Ansicht — der Katalog ersetzt nur vorübergehend den
 * Inhalt der Spalte (kein Modal), damit er wie eine Unteransicht des Chats wirkt.
 */
const SKILL_GROUPS: { icon: string; label: string; examples: string[] }[] = [
  { icon: "request_quote", label: "Anfragen & Angebote", examples: ["Angebot für 80 Gäste, Fingerfood", "Zweite Variante, 15 % günstiger", "Anfrage aus dieser E-Mail anlegen"] },
  { icon: "menu_book", label: "Karten & Katalog", examples: ["Lies diese Speisekarte ein", "Allergene ergänzen (LMIV)", "Preise um 5 % anheben"] },
  { icon: "forum", label: "Kommunikation", examples: ["Erinnere Kessler ans Angebot", "Antwort auf diese Mail entwerfen", "Zahlungserinnerung R-2041"] },
  { icon: "insights", label: "Zahlen & Nachschlagen", examples: ["Wie lief der Juni?", "Wer schuldet mir noch Geld?", "Umsatz Weber & Sturm gesamt"] },
  { icon: "schedule", label: "Merken & Automatisieren", examples: ["Merk dir: montags liefern wir nicht", "Erinnere mich 3 Tage vor Ablauf"] },
];

/**
 * Ruhezustand (23d): bis zu drei echte, faellige Dinge als Vorschlag - identische Auswahl wie im
 * Dashboard (`tasks/board`), keine erfundenen Zeiten oder Aktivitaeten. Ein Klick uebernimmt einen
 * passenden Formulierungsvorschlag ins Eingabefeld (nicht sofort senden, wie EMPTY_SUGGESTIONS).
 */
const SUGGESTION_META: Partial<Record<AutoTaskKind, { verb: string; icon: string; prompt: (title: string) => string }>> = {
  payment_overdue: { verb: "Erinnerung schreiben", icon: "payments", prompt: (t) => `Schreib eine freundliche Zahlungserinnerung für: ${t}` },
  inquiry_stale: { verb: "Angebot erzeugen", icon: "mail", prompt: (t) => `Erstelle ein Angebot für: ${t}` },
};
// E1: vorgeschlagen wird `offer_action_needed`, nicht mehr das wartende Angebot (s. `angebotsVorschlag`).
const CHAT_SUGGESTION_KINDS: AutoTaskKind[] = ["payment_overdue", "offer_action_needed", "inquiry_stale"];

/** Tageszeit-Gruß (wie claude.ai). Datum lokal — reine Anzeige, kein State. */
function greetingFor(hour: number): string {
  if (hour < 5) return "Guten Abend";
  if (hour < 11) return "Guten Morgen";
  if (hour < 18) return "Guten Tag";
  return "Guten Abend";
}

/**
 * Phase G v1 — Modul-Guard fuer den proaktiven Einstieg: einmal je App-Session (Modul-Lebensdauer,
 * ueberlebt Remounts der Spalte, wird nur durch einen echten Reload zurueckgesetzt). Verhindert, dass
 * das Auto-Briefing sich innerhalb derselben Sitzung wiederholt.
 */
let autoBriefedThisSession = false;

/**
 * 23e: gruppiert Unterhaltungen nach Tag (HEUTE/GESTERN/ÄLTER) fuer das Verlauf-Dropdown - reine
 * Anzeige-Logik auf Basis der bereits geladenen Liste, keine eigene Datenquelle.
 */
function groupConversationsByDay(items: AgentConversationSummary[]): { label: string; items: AgentConversationSummary[] }[] {
  const dayStart = (iso: string) => {
    const d = new Date(iso);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 24 * 60 * 60 * 1000;
  const buckets: Record<"HEUTE" | "GESTERN" | "ÄLTER", AgentConversationSummary[]> = { HEUTE: [], GESTERN: [], ÄLTER: [] };
  for (const c of items) {
    const day = dayStart(c.lastMessageAt);
    if (day === today) buckets.HEUTE.push(c);
    else if (day === yesterday) buckets.GESTERN.push(c);
    else buckets["ÄLTER"].push(c);
  }
  return (["HEUTE", "GESTERN", "ÄLTER"] as const).map((label) => ({ label, items: buckets[label] })).filter((g) => g.items.length > 0);
}

export function CopilotPanel() {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const {
    isOpen,
    close,
    floatWin,
    session,
    activeConversationId,
    conversations,
    conversationsLoading,
    startNewConversation,
    switchConversation,
    reloadConversations,
  } = useCopilot();
  const { messages, cards, steps, busy, loading, error, setError, send, runAction, approve, reject } = session;
  // Auf Mobil (floatWin inaktiv) ist der offene Copilot eine volle Schublade wie jeder andere
  // Drawer - dieselbe "Overlay offen"-Meldung wie dort (s. Kommentar am Hook). Ab lg schwebt/
  // dockt er nur an, kein Vollbild-Overlay, deshalb dort kein Signal.
  useOverlayOpenSignal(isOpen && !floatWin.active);
  const user = useUser();
  // 23d (Ruhezustand): dieselbe berechnete Auto-Task-Liste wie Dashboard/Aufgaben (eine Quelle
  // der Wahrheit) - keine erfundenen Vorschlaege, nur echte faellige Dinge.
  const boardQ = useApi<TasksBoard>("/api/tasks/board");
  const [text, setText] = useState("");
  // K3b: Umbenennen im Verlauf-Dropdown - id der Zeile im Bearbeitungsmodus + Eingabewert.
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Eingabefeld wächst mit dem Text (Antoine, 12.10.2026), bis max-h-40, danach scrollt es.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);
  const scrollRef = useRef<HTMLDivElement>(null);
  // 23c: "chat" (normaler Verlauf/Eingabe) oder "skills" (durchsuchbarer Fähigkeiten-Katalog,
  // ersetzt voruebergehend den Inhalt der Spalte statt eines echten Modals).
  const [view, setView] = useState<"chat" | "skills">("chat");
  const [skillQuery, setSkillQuery] = useState("");
  // 23e: Verlauf-Dropdown (Unterhaltungen wechseln/neu anlegen).
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyRef = useRef<HTMLDivElement>(null);

  // Vorname für die Begrüßung: Anzeigename bevorzugt, sonst der lokale Teil der E-Mail.
  const firstName = user?.displayName?.trim().split(/\s+/)[0] || user?.primaryEmail?.split("@")[0] || "";
  const greeting = greetingFor(new Date().getHours());

  useEffect(() => {
    if (!isOpen) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, cards, busy, isOpen]);

  /**
   * K2.5 (Warte-Erlebnis): Laeuft gerade eine Komposition, tritt an die Stelle der Zeile
   * „MAESTRO arbeitet …" eine Karte, die sich mit dem ECHTEN Stand fuellt. Quelle ist
   * `inquiries.generation_phase` — dasselbe Feld, das die Anfragenseite pollt (kein zweiter
   * Fortschrittsweg, siehe ui/komposition-fortschritt.tsx).
   *
   * Abgefragt wird nur, waehrend ein Turn laeuft UND die Spalte offen ist: eine zugeklappte
   * Spalte hat niemanden, dem sie Fortschritt zeigen koennte.
   */
  const laufendeKomposition = useLaufendeKomposition(busy && isOpen);
  /**
   * Die einzige Zahl der Karte, und sie ist gemessen: die Zeit seit dem Start dieses Turns.
   * Der 1-Sekunden-Takt ist die Anzeige-Aufloesung des Zaehlers (m:ss) — kein Grenzwert, keine
   * Annahme ueber die Laufzeit.
   */
  const [wartetSeitMs, setWartetSeitMs] = useState(0);
  useEffect(() => {
    if (!busy) {
      setWartetSeitMs(0);
      return;
    }
    const start = Date.now();
    setWartetSeitMs(0);
    const iv = setInterval(() => setWartetSeitMs(Date.now() - start), 1000);
    return () => clearInterval(iv);
  }, [busy]);

  // Beim Schliessen der Spalte zurueck in den normalen Chat wechseln - sonst zeigt ein spaeteres
  // Wiederoeffnen faelschlich noch den Fähigkeiten-Katalog vom letzten Mal.
  useEffect(() => {
    if (!isOpen) {
      setView("chat");
      setSkillQuery("");
      setHistoryOpen(false);
    }
  }, [isOpen]);

  // 23e: Verlauf-Dropdown bei Klick ausserhalb oder Escape schliessen.
  useEffect(() => {
    if (!historyOpen) return;
    const onDown = (e: MouseEvent) => {
      if (historyRef.current && !historyRef.current.contains(e.target as Node)) setHistoryOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setHistoryOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [historyOpen]);

  function addFiles(list: FileList | null) {
    if (!list) return;
    const picked = Array.from(list);
    const tooBig = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setError(`Datei zu groß: ${tooBig.name} (max. 8 MB).`);
      return;
    }
    setError(null);
    setFiles((prev) => [...prev, ...picked].slice(0, 4));
  }

  /** Beispiel-Prompt einer Karte ins Eingabefeld übernehmen und fokussieren (nicht sofort senden). */
  function useExample(example: string) {
    setText(example);
    // Nach dem Render fokussieren + Cursor ans Ende.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  }

  /** Beispiel aus dem Fähigkeiten-Katalog übernehmen: zurück zum Chat, Eingabefeld befüllen. */
  function useSkillExample(example: string) {
    setView("chat");
    useExample(example);
  }

  async function submit(promptText: string) {
    const trimmed = promptText.trim();
    // Das Modell sieht nur den Text, nicht den Datei-Inhalt. Bei einem Upload den/die Dateinamen
    // in die Nachricht geben — so hat der Agent Kontext ("ich sehe eine Speisekarte …") und ein
    // reiner Upload ohne Tippen erzeugt keine leere (vom Server abgelehnte) Nachricht.
    const names = files.map((f) => f.name).join(", ");
    const fileNote = files.length === 0 ? "" : files.length === 1 ? `Ich habe eine Datei angehängt: ${names}` : `Ich habe Dateien angehängt: ${names}`;
    const effectiveText = trimmed ? (fileNote ? `${trimmed}\n(${fileNote})` : trimmed) : fileNote;
    if (!effectiveText) return;
    /**
     * Antoines Befund 05.08.: Das Feld wurde erst NACH dem kompletten Agent-Turn geleert —
     * bei 30–170 s Laufzeit stand die Nachricht so lange im Eingabefeld, als wäre sie nie
     * abgeschickt worden. Jetzt: sofort leeren (wie jeder Chat), und NUR im Fehlerfall die
     * Eingabe wiederherstellen — eine getippte Nachricht darf nie verloren gehen.
     */
    const vorherText = promptText;
    const vorherFiles = files;
    setText("");
    setFiles([]);
    const ok = await send(effectiveText, vorherFiles);
    if (!ok) {
      setText(vorherText);
      setFiles(vorherFiles);
    }
  }

  const visibleMessages = messages.filter((m) => textOf(m).length > 0);
  const isEmpty = !loading && visibleMessages.length === 0 && cards.length === 0;

  // 23c: Live-Filter fuer den Fähigkeiten-Katalog - Gruppen ohne Treffer werden ausgeblendet,
  // sonst bleiben nur die passenden Beispielsätze einer Gruppe uebrig.
  const skillQueryNorm = skillQuery.trim().toLowerCase();
  const filteredSkillGroups = skillQueryNorm
    ? SKILL_GROUPS.map((g) => ({
        ...g,
        examples: g.examples.filter((ex) => ex.toLowerCase().includes(skillQueryNorm) || g.label.toLowerCase().includes(skillQueryNorm)),
      })).filter((g) => g.examples.length > 0)
    : SKILL_GROUPS;

  // 23d: bis zu drei echte, faellige Dinge (eine je Art) - dieselbe Auswahl-Logik wie im
  // Dashboard, nur kompakter (1 statt bis zu 4 je Art - hier ist nur Platz fuer wenige Karten).
  const suggestions = CHAT_SUGGESTION_KINDS.map((k) => boardQ.data?.auto.find((a) => a.kind === k)).filter((a): a is AutoTask => a != null);

  // K2 (Antoine, 01.08.2026): Die Kachel erschien nach JEDER Antwort - auch unter einer offenen
  // Rueckfrage. Jetzt erscheint sie nur im Leerlauf und je Unterhaltung einmal, solange die
  // Vorschlaege dieselben sind (Begruendung + Regeln: ui/vorschlags-anzeige.ts).
  const vorschlagsSig = vorschlagsSignatur(suggestions.map((a) => ({ kind: a.kind, id: a.signature })));
  // Zuletzt gezeigte Signatur je Unterhaltung - bewusst nur im Speicher: Nach einem Neuladen darf
  // die Kachel wieder erscheinen, das ist ein neuer Arbeitsbeginn.
  const gezeigtRef = useRef(new Map<string, string>());
  const konvKey = activeConversationId ?? "__default__";
  const letzterAssistentText = (() => {
    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      const m = visibleMessages[i]!;
      if (m.role !== "user") return textOf(m);
    }
    return "";
  })();
  const amThema = amThemaDran(
    (() => {
      for (let i = visibleMessages.length - 1; i >= 0; i--) if (visibleMessages[i]!.role === "user") return textOf(visibleMessages[i]!);
      return null;
    })(),
  );
  const vorschlaegeSichtbar = zeigeVorschlaege({
    amThemaDran: amThema,
    busy,
    hatOffeneFreigabe: cards.some((c) => c.type === "approval_gate"),
    letzterAssistentText,
    signatur: vorschlagsSig,
    zuletztGezeigt: gezeigtRef.current.get(konvKey) ?? null,
  });
  // Als "gezeigt" gilt die Kachel, sobald der Betreiber WEITERARBEITET, waehrend sie sichtbar war
  // (naechster Turn startet -> busy). Nicht beim Rendern selbst - sonst verschwaende sie sofort.
  const sichtbarRef = useRef(false);
  sichtbarRef.current = vorschlaegeSichtbar;
  const sigRef = useRef(vorschlagsSig);
  sigRef.current = vorschlagsSig;
  useEffect(() => {
    if (busy && sichtbarRef.current) gezeigtRef.current.set(konvKey, sigRef.current);
  }, [busy, konvKey]);

  // S1.7 (KONZEPT-GEDAECHTNIS.md § 4.2b "Web-Ignorieren"): es gibt keinen Klick-Handler zum
  // Ablehnen der Vorschlagskachel - Ignorieren ist implizit (gezeigt, aber nie angeklickt, bevor
  // sich die Signatur aendert). geklicktRef haelt fest, welche Signatur tatsaechlich angeklickt
  // wurde; der Effekt darunter vergleicht bei jedem Signaturwechsel gegen gezeigtRef und schreibt
  // "ignoriert" fuer eine Signatur, die gezeigt, aber nie geklickt wurde. Fire-and-forget, kein
  // Fehler hier darf den Chat je stoeren.
  const geklicktRef = useRef(new Set<string>());
  async function emitSpurVorschlag(angeboten: string, reagiert: "angenommen" | "ignoriert", umstand: Record<string, unknown> = {}) {
    try {
      await apiMutate("/api/gedaechtnis/spur", "POST", { angeboten, reagiert, umstand: { quelle: "vorschlagskachel", ...umstand } });
    } catch {
      /* Spur-Schreibfehler duerfen den Chat nie stoeren. */
    }
  }
  const vorherigeSigRef = useRef(vorschlagsSig);
  useEffect(() => {
    const vorherige = vorherigeSigRef.current;
    vorherigeSigRef.current = vorschlagsSig;
    if (vorherige === vorschlagsSig || vorherige === "") return;
    const warGezeigt = gezeigtRef.current.get(konvKey) === vorherige;
    if (warGezeigt && !geklicktRef.current.has(vorherige)) {
      void emitSpurVorschlag(`Vorschlagskachel: ${vorherige}`, "ignoriert");
    }
  }, [vorschlagsSig, konvKey]);

  /**
   * E4.7 — Selbstwahrnehmung, Ebene E1: was am Bestand auffällt, ungefragt und ohne Modell.
   *
   * Bewusst in die vorhandene „Soll ich noch etwas übernehmen?"-Fläche statt in eine eigene:
   * ein zweiter Hinweis-Kasten wäre ein zweiter Ort, den man wegklickt. Ein Fehler beim Abruf
   * bleibt still — eine Bestandsprüfung, die den Assistenten blockiert, wäre schlimmer als keine.
   */
  const [befunde, setBefunde] = useState<Befund[]>([]);
  // 05.08.: Befunde starten ZU — eine Zeile statt drei Kacheln (Antoines Platz-Befund).
  const [befundeOffen, setBefundeOffen] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    void (async () => {
      try {
        const res = await apiFetch("/api/agent/befunde");
        if (!res.ok) return;
        const body = (await res.json()) as { data?: { befunde?: Befund[] } };
        setBefunde(body.data?.befunde ?? []);
      } catch {
        /* still */
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || busy || autoBriefedThisSession) return;
    if (!isEmpty) return;
    autoBriefedThisSession = true;
    void (async () => {
      try {
        const res = await apiFetch("/api/assistant/briefing");
        if (!res.ok) return;
        const body = (await res.json()) as { data?: { hasPending?: boolean } };
        if (body.data?.hasPending) await send("Was steht an?");
      } catch {
        /* Briefing ist optional — ein Fehler darf den Copilot nie blockieren. */
      }
    })();
  }, [isOpen, busy, isEmpty, send]);

  return (
    <>
      {bestaetigungsDialog}
      <div
        onClick={close}
        aria-hidden
        className={`fixed inset-0 z-[55] bg-black/30 motion-safe:transition-opacity motion-safe:duration-200 lg:hidden dark:bg-black/50 ${isOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
      />
      <aside
        aria-label="MAESTRO KI Assistent"
        aria-hidden={!isOpen || (floatWin.active && floatWin.collapsed)}
        data-floating-window
        style={isOpen ? floatWin.style : undefined}
        className={`group/win fixed right-0 top-0 z-[56] flex h-full w-full max-w-[400px] flex-col border-l border-outline-variant/30 bg-surface-container-lowest motion-safe:transition-transform motion-safe:duration-200 dark:border-white/10 dark:bg-night-card ${isOpen ? "translate-x-0 shadow-2xl" : "translate-x-full"} ${
          !floatWin.active
            ? ""
            : floatWin.mode === "pinned"
              ? /* Fest verankert: keine Glas-Optik mehr, echte Spalte wie zuvor — verdraengt den
                   Hauptinhalt (siehe AppShell.tsx), statt ihn zu ueberlagern. Nicht ziehbar, nicht
                   skalierbar (Prototyp: .assistant.pinned, schwebender-assistent.html Z.315-323).
                   380px hier als STATISCHES Literal (Tailwind scannt den Quelltext, keine
                   JS-Variable) — muss mit PINNED_W in useFloatingWindow.ts synchron bleiben. */
                `${floatWin.collapsed ? "lg:hidden" : ""} lg:z-40 lg:max-w-none lg:w-[380px] lg:rounded-none lg:border-y-0 lg:border-r-0 lg:bg-surface-container-lowest lg:shadow-[-1px_0_0_rgba(20,16,10,0.08)] dark:lg:bg-night-card ${
                  floatWin.dragging ? "" : "motion-safe:lg:transition-[margin] motion-safe:lg:duration-200"
                }`
              : `${floatWin.collapsed ? "lg:hidden" : ""} lg:right-auto lg:top-auto lg:z-40 lg:max-w-none lg:translate-x-0 lg:rounded-[22px] lg:border lg:border-white/60 lg:bg-surface-container-lowest/75 lg:shadow-[0_28px_70px_-20px_rgba(38,33,25,0.35)] lg:backdrop-blur-2xl lg:backdrop-saturate-150 dark:lg:border-white/10 dark:lg:bg-night-card/72 ${
                  floatWin.dragging || floatWin.resizing ? "" : "motion-safe:lg:transition-[left,top,width,height] motion-safe:lg:duration-200"
                }`
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragOver) setDragOver(true);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <div
          onPointerDown={floatWin.onHeaderPointerDown}
          className={`flex items-center gap-2.5 border-b border-outline-variant/30 px-4 py-3 dark:border-white/10 ${
            floatWin.active && floatWin.mode !== "pinned" ? (floatWin.dragging ? "lg:cursor-grabbing" : "lg:cursor-grab") : ""
          }`}
        >
          {view === "skills" ? (
            <>
              <button
                type="button"
                onClick={() => setView("chat")}
                title="Zurück zum Chat"
                aria-label="Zurück zum Chat"
                className="rounded-full p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-background dark:text-surface-variant dark:hover:bg-white/5"
              >
                <Icon name="arrow_back" className="text-[20px]" />
              </button>
              <p className="min-w-0 flex-1 truncate text-body-sm font-semibold tracking-wide text-on-background dark:text-inverse-on-surface">Was MAESTRO kann</p>
            </>
          ) : (
            <>
              <Logo size={32} textClassName="text-[17px] leading-none" className="relative">
                {/* KI-Abzeichen (Prototyp: .orb-badge, Z.232-237/463-465) — macht "das ist KI" am
                    Avatar unmissverstaendlich. */}
                <span
                  aria-hidden="true"
                  className="absolute -bottom-1 -right-1 grid h-3.5 w-3.5 place-items-center rounded-full bg-surface-container-lowest text-primary ring-2 ring-surface-container-lowest dark:bg-night-card dark:ring-night-card"
                >
                  <Icon name="auto_awesome" className="text-[9px]" />
                </span>
              </Logo>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body-sm font-semibold tracking-wide text-on-background dark:text-inverse-on-surface">MAESTRO KI Assistent</p>
                <p className="truncate text-[11px] text-on-surface-variant dark:text-surface-variant">Dein Assistent — bedient die ganze Software</p>
              </div>
            </>
          )}
          {view !== "skills" && (
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => void startNewConversation()}
                title="Neuer Chat"
                aria-label="Neue Unterhaltung starten"
                className="rounded-full p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-background dark:text-surface-variant dark:hover:bg-white/5"
              >
                <Icon name="add" className="text-[20px]" />
              </button>
              <div className="relative" ref={historyRef}>
                <button
                  type="button"
                  onClick={() => setHistoryOpen((v) => !v)}
                  title="Verlauf"
                  aria-label="Unterhaltungs-Verlauf"
                  aria-haspopup="menu"
                  aria-expanded={historyOpen}
                  className={`rounded-full p-1.5 transition-colors hover:bg-surface-container-high dark:hover:bg-white/5 ${
                    historyOpen ? "text-primary" : "text-on-surface-variant hover:text-on-background dark:text-surface-variant"
                  }`}
                >
                  <Icon name="history" className="text-[20px]" />
                </button>
                {historyOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-50 mt-2 max-h-[70vh] w-[280px] overflow-y-auto rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-on-background shadow-2xl dark:border-white/10 dark:bg-night-card dark:text-inverse-on-surface"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        switchConversation(null);
                        setHistoryOpen(false);
                      }}
                      className={`flex w-full items-center gap-2 border-b border-outline-variant/20 px-3 py-2.5 text-left text-body-sm transition-colors hover:bg-surface-container-high dark:border-white/10 dark:hover:bg-white/5 ${
                        activeConversationId === null ? "font-semibold text-primary" : "text-on-background dark:text-inverse-on-surface"
                      }`}
                    >
                      <Icon name="forum" className="text-[16px]" />
                      <span className="truncate">Bisheriger Chat</span>
                    </button>
                    {conversationsLoading ? (
                      <p className="px-3 py-3 text-[12px] text-on-surface-variant dark:text-surface-variant">Lädt …</p>
                    ) : conversations.length === 0 ? (
                      <p className="px-3 py-3 text-[12px] text-on-surface-variant dark:text-surface-variant">Noch keine weiteren Unterhaltungen.</p>
                    ) : (
                      groupConversationsByDay(conversations).map((group) => (
                        <div key={group.label} className="py-1">
                          <p className="px-3 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wider text-on-surface-variant/80 dark:text-surface-variant/80">
                            {group.label}
                          </p>
                          {group.items.map((c) =>
                            renameId === c.id ? (
                              /* K3b: Inline-Umbenennen. Enter speichert, Escape bricht ab - kein
                                 Dialog, die Zeile selbst wird zur Eingabe. */
                              <div key={c.id} className="flex items-center gap-2 px-3 py-1.5">
                                <input
                                  autoFocus
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") setRenameId(null);
                                    if (e.key === "Enter" && renameValue.trim()) {
                                      const neu = renameValue.trim();
                                      setRenameId(null);
                                      void apiMutate(`/api/agent/conversations/${c.id}`, "PATCH", { title: neu })
                                        .then(() => reloadConversations())
                                        .catch(() => setError("Umbenennen hat nicht geklappt."));
                                    }
                                  }}
                                  maxLength={200}
                                  className="min-w-0 flex-1 rounded-lg border border-outline-variant/50 bg-surface-container-lowest px-2 py-1 text-body-sm text-on-background outline-none focus:border-primary dark:border-white/15 dark:bg-night-card dark:text-inverse-on-surface"
                                />
                              </div>
                            ) : (
                              <div
                                key={c.id}
                                className={`group/zeile flex w-full items-center gap-1 pr-2 transition-colors hover:bg-surface-container-high dark:hover:bg-white/5 ${
                                  activeConversationId === c.id ? "font-semibold text-primary" : "text-on-background dark:text-inverse-on-surface"
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    switchConversation(c.id);
                                    setHistoryOpen(false);
                                  }}
                                  className="flex min-w-0 flex-1 items-center justify-between gap-2 px-3 py-2 text-left text-body-sm"
                                >
                                  <span className="min-w-0 flex-1 truncate">{c.title || "Unbenannte Unterhaltung"}</span>
                                  <span className="shrink-0 text-[10.5px] text-on-surface-variant dark:text-surface-variant">
                                    {new Date(c.lastMessageAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  aria-label="Unterhaltung umbenennen"
                                  title="Umbenennen"
                                  onClick={() => {
                                    setRenameId(c.id);
                                    setRenameValue(c.title ?? "");
                                  }}
                                  className="shrink-0 rounded p-1 text-on-surface-variant opacity-0 transition-opacity hover:bg-surface-container-highest group-hover/zeile:opacity-100 dark:hover:bg-white/10"
                                >
                                  <Icon name="edit" className="text-[14px]" />
                                </button>
                                {/* E4.9/E5 — DSGVO: der Verlauf wird wirklich geloescht, nicht
                                    nur ausgeblendet. Der Chat liegt im Durable Object, nicht in
                                    der Datenbank; die Route raeumt erst dort auf und entfernt
                                    danach die Zeile. Bewusst mit Rueckfrage: Ein Verlauf ist
                                    unwiederbringlich weg, und der Knopf steht direkt neben dem
                                    zum Umbenennen. */}
                                <button
                                  type="button"
                                  aria-label="Unterhaltung löschen"
                                  title="Löschen"
                                  onClick={async () => {
                                    if (
                                      !(await bestaetigen({
                                        titel: `„${c.title || "Unbenannte Unterhaltung"}“ endgültig löschen?`,
                                        text: "Der Gesprächsverlauf ist danach nicht wiederherstellbar.",
                                        bestaetigen: "Löschen",
                                        gefahr: true,
                                      }))
                                    )
                                      return;
                                    void apiMutate(`/api/agent/conversations/${c.id}`, "DELETE")
                                      .then(() => {
                                        if (activeConversationId === c.id) switchConversation(null);
                                        reloadConversations();
                                      })
                                      .catch(() => setError("Löschen hat nicht geklappt — die Unterhaltung ist noch da."));
                                  }}
                                  className="shrink-0 rounded p-1 text-on-surface-variant opacity-0 transition-opacity hover:bg-error/10 hover:text-error group-hover/zeile:opacity-100"
                                >
                                  <Icon name="delete" className="text-[14px]" />
                                </button>
                              </div>
                            ),
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Fest verankern (nur ab lg sinnvoll — auf Mobil ist keine Spalte neben dem Inhalt
              Platz). Prototyp: #pinBtn, schwebender-assistent.html Z.473-475/740-757. */}
          <button
            type="button"
            onClick={floatWin.togglePin}
            aria-pressed={floatWin.mode === "pinned"}
            title={floatWin.mode === "pinned" ? "Lösen (wieder frei schweben)" : "Fest verankern (rechte Spalte)"}
            aria-label={floatWin.mode === "pinned" ? "Lösen (wieder frei schweben)" : "Fest verankern"}
            className={`hidden rounded-full p-1.5 transition-colors hover:bg-surface-container-high lg:inline-flex dark:hover:bg-white/5 ${
              floatWin.mode === "pinned" ? "text-primary" : "text-on-surface-variant hover:text-on-background dark:text-surface-variant"
            }`}
          >
            <Icon name="push_pin" className="text-[20px]" />
          </button>
          {/* Ab lg klappt dieser Knopf auf die Randleiste ein (Prototyp: #collapseBtn) statt die
              ganze Spalte zu schliessen — der globale Ein-/Ausblenden-Knopf in der Topbar
              (toggleCopilot) bleibt der App-weite An/Aus-Schalter, siehe Kommentar am Hook. */}
          <button
            type="button"
            onClick={() => (floatWin.active ? floatWin.collapse() : close())}
            title="KI-Assistent einklappen"
            aria-label="KI-Assistent einklappen"
            className="rounded-full p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-background dark:text-surface-variant dark:hover:bg-white/5"
          >
            <Icon name="close" className="text-[20px]" />
          </button>
        </div>

        <div ref={scrollRef} className="relative flex-1 overflow-y-auto px-4 pb-4 pt-4">
          {dragOver && (
            <div className="pointer-events-none absolute inset-0 z-10 m-2 grid place-items-center rounded-2xl border-2 border-dashed border-primary bg-primary/5 text-body-sm font-semibold text-primary">
              Datei hier ablegen, um sie einzulesen
            </div>
          )}
          {view === "skills" ? (
            <div className="flex flex-col gap-4 pt-1">
              <label className="relative block">
                <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[17px] text-on-surface-variant/70" />
                <input
                  type="text"
                  value={skillQuery}
                  onChange={(e) => setSkillQuery(e.target.value)}
                  placeholder="Fähigkeit suchen — z. B. „Rechnung“"
                  className="w-full rounded-full border border-outline-variant bg-surface-container-lowest py-2 pl-9 pr-3 text-body-sm text-on-background outline-none placeholder:text-on-surface-variant/60 dark:placeholder:text-on-surface-variant/80 focus:border-primary/50 dark:bg-white/[0.04] dark:text-inverse-on-surface"
                  autoFocus
                />
              </label>
              <p className="text-[12px] leading-relaxed text-on-surface-variant dark:text-surface-variant">
                Tippe einen Satz an, dann übernimmt MAESTRO ihn ins Eingabefeld — du kannst ihn vorher noch ändern.
              </p>
              {filteredSkillGroups.length === 0 ? (
                <p className="py-6 text-center text-body-sm text-on-surface-variant dark:text-surface-variant">Keine Fähigkeit gefunden für „{skillQuery}“.</p>
              ) : (
                filteredSkillGroups.map((g) => (
                  <div key={g.label} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-primary/10 text-primary dark:bg-primary/20">
                        <Icon name={g.icon} className="text-[14px]" />
                      </span>
                      <span className="text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{g.label}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pl-8">
                      {g.examples.map((ex) => (
                        <button
                          key={ex}
                          type="button"
                          onClick={() => useSkillExample(ex)}
                          className="rounded-full border border-outline-variant px-3 py-1.5 text-left text-[12px] text-on-surface-variant transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-on-background dark:border-white/10 dark:text-surface-variant dark:hover:bg-white/[0.06]"
                        >
                          „{ex}“
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
              <div className="mt-1 flex items-start gap-2.5 rounded-2xl bg-surface-container-high px-3.5 py-3 dark:bg-white/[0.04]">
                <Icon name="help_outline" className="mt-0.5 shrink-0 text-[16px] text-on-surface-variant dark:text-surface-variant" />
                <p className="text-[12px] leading-relaxed text-on-surface-variant dark:text-surface-variant">
                  Du musst nichts auswendig lernen: schreib es so, wie du es einem Mitarbeiter sagen würdest. MAESTRO fragt nach, wenn etwas fehlt.
                </p>
              </div>
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col gap-5 pt-3">
              <div className="flex flex-col gap-2.5">
                {firstName && (
                  <p className="text-body-sm font-medium text-on-surface-variant dark:text-surface-variant">
                    {greeting}, {firstName}.
                  </p>
                )}
                <h2 className="font-serif text-[26px] font-semibold italic leading-tight text-on-background dark:text-inverse-on-surface">Sag&rsquo;s mir einfach.</h2>
                <p className="text-body-sm leading-relaxed text-on-surface-variant dark:text-surface-variant">
                  Alles, was du hier klicken kannst, kannst du mir auch schreiben — Karten einlesen, Angebote bauen,
                  nachfassen, nachschlagen. Ich frage nach, bevor etwas rausgeht.
                </p>
              </div>

              <div className="flex flex-col gap-2.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant/80 dark:text-surface-variant/80">Womit soll ich anfangen?</p>
                <div className="flex flex-col gap-2">
                  {EMPTY_SUGGESTIONS.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => (s.action === "upload" ? fileInputRef.current?.click() : useExample(s.example))}
                      disabled={busy}
                      className="group flex items-start gap-3 rounded-2xl border border-outline-variant bg-surface-container-lowest px-3.5 py-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
                    >
                      <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20">
                        <Icon name={s.icon} className="text-[18px]" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{s.label}</span>
                        <span className="block truncate text-[12px] italic text-on-surface-variant dark:text-surface-variant">„{s.example}“</span>
                      </span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setView("skills")}
                  className="mt-0.5 flex items-center justify-center gap-1.5 rounded-2xl border border-dashed border-outline-variant px-3.5 py-2.5 text-[12px] font-medium text-on-surface-variant transition-colors hover:border-primary/40 hover:text-primary dark:border-white/15 dark:text-surface-variant"
                >
                  Alle Fähigkeiten ansehen <Icon name="arrow_forward" className="text-[15px]" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {visibleMessages.map((m) => {
                const text = textOf(m);
                const contextLabel = m.role === "user" ? internalContextLabel(text) : null;
                if (contextLabel) {
                  return (
                    <div key={m.id} className="mx-auto flex items-center gap-1.5 rounded-full bg-surface-container-low px-3 py-1.5 text-[11px] text-on-surface-variant dark:bg-white/[0.04] dark:text-surface-variant">
                      <Icon name="info" className="text-[13px]" />
                      {contextLabel}
                    </div>
                  );
                }
                return m.role === "user" ? (
                  <div key={m.id} className="ml-auto max-w-[85%]">
                    <div className="rounded-[12px_12px_3px_12px] bg-surface-container-high px-3.5 py-2.5 text-body-sm text-on-background dark:bg-surface-variant">
                      {text}
                    </div>
                  </div>
                ) : (
                  <div key={m.id} className="flex flex-col gap-1.5">
                    <span className="text-label-md font-semibold uppercase tracking-wide text-on-surface-variant dark:text-surface-variant">MAESTRO</span>
                    <div className="whitespace-pre-wrap text-body-sm leading-relaxed text-on-background dark:text-inverse-on-surface"><ChatText text={stripMarkdown(text)} /></div>
                  </div>
                );
              })}
              {/* P5 (Antoines Festlegung 30.07.2026): "Der Benutzer soll sehen, was der Chat macht.
                  Nicht im Stream, aber Zeile fuer Zeile." Eine Zeile je Werkzeugaufruf - was getan
                  wurde, woran, mit welchem Ergebnis.

                  Die Zeilen BLEIBEN STEHEN (nicht eingeklappt): Wer sehen soll, was passiert ist,
                  soll dafuer nicht klicken muessen. Der Verlauf ist damit zugleich das Protokoll.

                  aria-live="polite": Ein Fortschritt, den ein Screenreader nicht vorliest,
                  existiert fuer einen Teil der Nutzer nicht. "polite" statt "assertive", weil die
                  Zeilen begleiten und nicht unterbrechen sollen.

                  Ohne Farbe verstaendlich: Das Vorzeichen (Haken/Kreuz) traegt die Aussage, die
                  Farbe verstaerkt sie nur. */}
              {steps.length > 0 &&
                (() => {
                  /**
                   * Antoines Befund 05.08.: Die Zeilen wiederholten sich („Kontext: Angebot X"
                   * dreimal, „Vorgänge angesehen" doppelt) und fraßen Platz. Verdichtung OHNE
                   * die Festlegung aus P0.1 zu brechen (Zeilen bleiben stehen, kein Einklappen):
                   *  - direkte Wiederholungen werden EINE Zeile mit „(N×)",
                   *  - „Kontext: …" erscheint nur, wenn sich der Kontext wirklich ÄNDERT —
                   *    derselbe Kontext erneut gesetzt ist Buchhaltung, keine Information.
                   */
                  const kompakt: { text: string; ok: boolean; n: number }[] = [];
                  let letzterKontext: string | null = null;
                  for (const s of steps) {
                    if (s.text.startsWith("Kontext:")) {
                      if (s.ok && s.text === letzterKontext) continue;
                      letzterKontext = s.text;
                    }
                    const vorher = kompakt[kompakt.length - 1];
                    if (vorher && vorher.text === s.text && vorher.ok === s.ok) {
                      vorher.n += 1;
                      continue;
                    }
                    kompakt.push({ text: s.text, ok: s.ok, n: 1 });
                  }
                  return (
                    <ul
                      aria-live="polite"
                      aria-label="Was MAESTRO getan hat"
                      className="flex flex-col gap-1 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.03]"
                    >
                      {kompakt.map((s, i) => (
                        <li key={`${s.text}-${i}`} className="flex items-start gap-2 font-body-sm text-[12px] leading-relaxed">
                          <span aria-hidden="true" className={s.ok ? "text-secondary dark:text-[#b8ae9e]" : "text-error dark:text-[#e39b92]"}>
                            {s.ok ? "✓" : "✗"}
                          </span>
                          <span className="sr-only">{s.ok ? "Erledigt:" : "Nicht erledigt:"}</span>
                          <span className="text-on-surface-variant dark:text-inverse-on-surface/80">
                            {s.text}
                            {s.n > 1 ? ` (${s.n}×)` : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              {cards.map((card, i) => (
                <CardView key={`${card.type}-${i}`} card={card} busy={busy} onAction={runAction} onApprove={approve} onReject={reject} onSend={send} />
              ))}
              {/* K2.5: Eine Komposition dauert gemessene 70–91 s (LOOP-KI-AGENT K0.7/K1.5). So
                  lange nur „MAESTRO arbeitet …" zu zeigen, ist von „hängt" nicht zu
                  unterscheiden — also zeigt die Karte, was wirklich schon geschehen ist. Laeuft
                  keine Komposition (der Normalfall, Antworten in Sekunden), bleibt es bei der
                  schlichten Zeile. */}
              {busy &&
                (laufendeKomposition ? (
                  <KompositionsFortschritt lauf={laufendeKomposition} wartetSeitMs={wartetSeitMs} />
                ) : (
                  <div className="flex items-center gap-2 text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary/20 border-t-primary" /> MAESTRO arbeitet …
                  </div>
                ))}
              {/* Antoines Befund 05.08.: die Hinweis-Kacheln fraßen den Chat-Platz. Jetzt EINE
                  zugeklappte Zeile; aufgeklappt schickt der Klick auf eine Handlung den Auftrag
                  IN DEN CHAT (MAESTRO führt aus) statt nur zu navigieren. */}
              {befunde.length > 0 && !amThema && (
                <div className="mt-1 rounded-2xl border border-outline-variant bg-surface-container-low dark:border-white/10 dark:bg-white/[0.03]">
                  <button
                    type="button"
                    onClick={() => setBefundeOffen((v) => !v)}
                    className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left"
                    aria-expanded={befundeOffen}
                  >
                    <span className="flex items-center gap-2 text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                      <Icon
                        name={befunde.some((b) => b.schwere === "hoch") ? "priority_high" : "info"}
                        className={`text-[16px] ${befunde.some((b) => b.schwere === "hoch") ? "text-error" : "text-primary"}`}
                      />
                      {befunde.length === 1 ? "1 Hinweis zum Bestand" : `${befunde.length} Hinweise zum Bestand`}
                    </span>
                    <Icon name={befundeOffen ? "expand_less" : "expand_more"} className="text-[18px] text-on-surface-variant" />
                  </button>
                  {befundeOffen && (
                    <div className="flex flex-col gap-2 px-3 pb-3">
                      {befunde.map((b) => (
                        <button
                          key={b.kennung}
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            setBefundeOffen(false);
                            void send(`${b.handlung.text} — es geht um: ${b.klartext}`);
                          }}
                          className="group flex items-start gap-3 rounded-2xl border border-outline-variant bg-surface-container-lowest px-3.5 py-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
                        >
                          <span
                            className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg ${b.schwere === "hoch" ? "bg-error/10 text-error dark:bg-error/20" : "bg-primary/10 text-primary dark:bg-primary/20"}`}
                          >
                            <Icon name={b.schwere === "hoch" ? "priority_high" : "info"} className="text-[18px]" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{b.klartext}</span>
                            <span className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold text-primary">
                              <Icon name="chat" className="text-[13px]" /> {b.handlung.text} — im Chat erledigen
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {vorschlaegeSichtbar && (
                <div className="mt-1 flex flex-col gap-3 rounded-2xl border border-outline-variant bg-surface-container-low px-4 py-4 dark:border-white/10 dark:bg-white/[0.03]">
                  <div>
                    <p className="font-serif text-[17px] font-semibold italic leading-tight text-on-background dark:text-inverse-on-surface">Soll ich noch etwas übernehmen?</p>
                    <p className="mt-1 text-[12px] leading-relaxed text-on-surface-variant dark:text-surface-variant">
                      Ein paar Dinge, die ich gerade sinnvoll finde. Antippen genügt — ich zeige dir alles vorher.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {suggestions.map((s) => {
                      const angebot = s.kind === "offer_action_needed" ? angebotsVorschlag(s) : null;
                      const basis = SUGGESTION_META[s.kind] ?? { verb: "Ansehen", icon: "task_alt", prompt: (t: string) => t };
                      const meta = angebot ? { verb: angebot.verb, icon: "priority_high", prompt: () => angebot.prompt } : basis;
                      const titel = angebot?.titel ?? s.title;
                      const hinweis = angebot ? angebot.hinweis : s.dueHint;
                      return (
                        <button
                          key={s.signature}
                          type="button"
                          onClick={() => {
                            geklicktRef.current.add(vorschlagsSig);
                            void emitSpurVorschlag(`Vorschlagskachel: ${s.kind}`, "angenommen", { kind: s.kind });
                            useExample(meta.prompt(s.title));
                          }}
                          className="group flex items-start gap-3 rounded-2xl border border-outline-variant bg-surface-container-lowest px-3.5 py-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/5 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
                        >
                          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20">
                            <Icon name={meta.icon} className="text-[18px]" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{titel}</span>
                            {hinweis && <span className="block truncate text-[11.5px] text-on-surface-variant dark:text-surface-variant">{hinweis}</span>}
                            <span className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold text-primary">
                              <Icon name="auto_awesome" className="text-[13px]" /> {meta.verb}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-2 pt-0.5">
                    <button
                      type="button"
                      onClick={() => setView("skills")}
                      className="rounded-full border border-outline-variant px-3 py-1.5 text-[12px] font-medium text-primary transition-colors hover:bg-primary/5 dark:border-white/10"
                    >
                      Was kannst du noch?
                    </button>
                    <button
                      type="button"
                      onClick={() => useExample("Gib mir einen kurzen Tagesüberblick.")}
                      className="rounded-full border border-outline-variant px-3 py-1.5 text-[12px] font-medium text-on-surface-variant transition-colors hover:border-primary/40 hover:text-primary dark:border-white/10 dark:text-surface-variant"
                    >
                      Tagesüberblick
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {view !== "skills" && (
        <div className="border-t border-outline-variant/30 bg-surface-container-lowest px-4 py-3 dark:border-white/10 dark:bg-night-bar">
          {error && (
            <div className="mb-2">
              <ErrorNote message={error} />
            </div>
          )}
          {files.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {files.map((f, i) => (
                <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-outline-variant px-2.5 py-1 text-label-md text-on-surface-variant">
                  <Icon name={fileIcon(f)} className="text-[15px]" />
                  {f.name}
                  <button type="button" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                    <Icon name="close" className="text-[15px]" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit(text);
            }}
            className="flex items-end gap-2 rounded-[13px] border border-outline-variant bg-surface-container-lowest p-2 pl-3 dark:bg-inverse-surface"
          >
            <Button variant="ghost" className="shrink-0 gap-1 px-2" onClick={() => fileInputRef.current?.click()} disabled={busy} aria-label="Datei anhängen">
              <Icon name="attach_file" className="text-[19px]" />
            </Button>
            <input ref={fileInputRef} type="file" accept={FILE_ACCEPT} multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void submit(text);
                }
              }}
              rows={1}
              placeholder="Frag den KI-Assistenten …"
              className="max-h-40 flex-1 resize-none bg-transparent py-2 text-body-sm text-on-background outline-none placeholder:text-on-surface-variant/60 dark:placeholder:text-on-surface-variant/80 dark:text-inverse-on-surface"
            />
            <button
              type="submit"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-on-primary disabled:opacity-50"
              disabled={busy || (text.trim() === "" && files.length === 0)}
              aria-label="Senden"
            >
              <Icon name="arrow_upward" className="text-[18px]" />
            </button>
          </form>
          <p className="mt-2 text-center text-label-md text-on-surface-variant/70">
            MAESTRO schlägt vor — gesendet wird erst nach deiner Freigabe.
          </p>
        </div>
        )}
        {/* Skalier-Griffe an allen VIER Ecken (Prototyp: .resize-handle[data-corner], Z.292-309/493-504).
            Ueberlappungs-Fix aus dem Prototyp uebernommen, ABER pointer-events bewusst NICHT
            zusaetzlich hover-gegated (Abweichung vom Prototyp-CSS): klein (14px) und exakt in der
            Rundungsecke (0/0 statt eingerueckt) reicht bereits, um Header-Avatar/Collapse-Knopf und
            den Composer-Sendeknopf nie zu beruehren (beide sitzen durch ihr eigenes Padding min.
            12-16px von der Kante entfernt — geprueft gegen die tatsaechlichen Tailwind-Klassen).
            `pointer-events:none bis :hover` wie im Prototyp wuerde den Griff dagegen fuer JEDEN
            Zeiger unerreichbar machen, der nicht zuvor schon woanders auf dem Fenster war (Playwright-
            Actionability-Check UND ein sehr gezielter erster Klick treffen exakt dieses Problem:
            :hover auf dem Griff kann nicht entstehen, solange genau die Flaeche, die dafuer
            angepeilt wird, selbst pointer-events:none traegt) — deshalb hier nur die OPTIK
            (opacity) hover-gegated, die Griffe bleiben immer klickbar. Nur im "float"-Modus
            sichtbar (angedockt: nicht skalierbar). */}
        {floatWin.active &&
          floatWin.mode === "float" &&
          RESIZE_CORNERS.map((c) => (
            <div
              key={c.corner}
              onPointerDown={(e) => floatWin.onResizeHandlePointerDown(e, c.corner)}
              role="separator"
              aria-label={`Fenstergröße anpassen (${c.label})`}
              title={`Ziehen zum Skalieren (${c.label})`}
              data-corner={c.corner}
              className={`absolute z-10 hidden h-3.5 w-3.5 items-center justify-center opacity-0 transition-opacity duration-150 group-hover/win:opacity-100 lg:flex ${c.pos} ${c.cursor} ${
                floatWin.resizing ? "!opacity-100" : ""
              }`}
            >
              <Icon name="open_in_full" className={`pointer-events-none text-[9px] text-on-surface-variant/60 ${c.rotate}`} />
            </div>
          ))}
      </aside>
      {/* Randleiste im eingeklappten Zustand (Prototyp: #edgeTab, Z.328-344/507-515) — feste Leiste
          am rechten Rand statt komplettem Verschwinden, immer an derselben Stelle. Nur ab lg (Mobil
          bleibt bei "ganz zu/auf" ueber den Topbar-Knopf, siehe Kommentar am Hook). */}
      {floatWin.active && isOpen && floatWin.collapsed && (
        <button
          type="button"
          onClick={floatWin.expand}
          aria-label="KI-Assistent wieder öffnen"
          title="KI-Assistent öffnen"
          className="fixed right-0 top-1/2 z-40 hidden h-24 w-11 -translate-y-1/2 flex-col items-center justify-center gap-2 rounded-l-[18px] border border-r-0 border-white/60 bg-surface-container-lowest/85 shadow-[-14px_14px_32px_-14px_rgba(38,33,25,0.3)] backdrop-blur-2xl backdrop-saturate-150 transition-[width,transform] hover:w-[50px] hover:-translate-x-0.5 hover:-translate-y-1/2 lg:flex dark:border-white/10 dark:bg-night-card/85"
        >
          <Logo size={28} textClassName="text-[14px] leading-none" className="relative" ariaHidden>
            <span className="absolute -bottom-1 -right-1 grid h-3 w-3 place-items-center rounded-full bg-surface-container-lowest text-primary ring-2 ring-surface-container-lowest dark:bg-night-card dark:ring-night-card">
              <Icon name="auto_awesome" className="text-[7px]" />
            </span>
          </Logo>
          <Icon name="chevron_left" className="text-[14px] text-on-surface-variant" />
        </button>
      )}
    </>
  );
}

/** Icon je Anhang-Typ: Tabelle, PDF oder Bild. */
function fileIcon(f: File): string {
  const name = f.name.toLowerCase();
  if (f.type.includes("sheet") || f.type.includes("excel") || f.type === "text/csv" || /\.(xlsx|xls|csv)$/.test(name)) return "table_view";
  if (f.type === "application/pdf") return "picture_as_pdf";
  return "image";
}
